import { useState, Fragment, useRef, useEffect } from "react";
import { useStore } from "../../contexts/StoreContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useNotification } from "../../contexts/NotificationContext";
import { Dialog, Transition } from "@headlessui/react";
import { useAuth } from "../../contexts/AuthContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createPassLayout } from "../../api/mutations";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchPasses } from "../../api/queries";
import {
  stampIconSource,
  getCropModalTitle,
  validateMilestonesForSave,
  validateTierForSave,
  validateCouponForSave,
  openMilestoneIconCrop,
} from "./passEditorHelpers";
import defaultBackground from "../../assets/background.png";
import TrialEndedAlert from "../../components/TrialEndedAlert";
import ImageCropModal, { CROP_TYPES } from "../../components/ImageCropModal";
import {
  generateStampedImages,
  createPreviewUrls,
  appendStampedImages,
  generatePreviewImage,
} from "../../services/imageService";
import PassPreview from "./components/PassPreview";
import PassForm from "./components/PassForm";
import Header from "../../components/Header";
import Button from "../../components/Button";
import RecoverableBoundary from "../../components/RecoverableBoundary";
import { notifyError } from "../../utils/bugsnag";
import {
  extractMilestoneIconUploads,
  rewardFieldPreview,
  newMilestoneId,
  walletLabels,
  walletLabelsSignature,
} from "../../utils/passUtils";

export default function NewPass() {
  const { t } = useLanguage();
  const { showNotification } = useNotification();
  // Owned here, not in PassForm: both save buttons go through
  // handleSaveLayout, which is the only place that can see every attempt.
  const [saveAttempted, setSaveAttempted] = useState(false);
  // Field labels the wallet itself prints — mirrored so the preview reads like
  // the real card instead of hardcoded English.
  const wl = walletLabels(t);
  const wlSignature = walletLabelsSignature(wl);
  const navigate = useNavigate();
  const { selectedStore } = useStore();
  const { user } = useAuth();
  const [isTrialEndedAlertOpen, setIsTrialEndedAlertOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [useCustomTerms, setUseCustomTerms] = useState(false);
  const [termsUrl, setTermsUrl] = useState("");

  const [formData, setFormData] = useState({
    name: "Card #1",
    type: "stamp",
    company: selectedStore?.name || "Coffee Inc.",
    companyLogo: selectedStore?.logo_url || "https://placehold.co/64x64",
    backgroundColor: selectedStore?.background_color || "#F7F6F2",
    secondaryColor: selectedStore?.secondary_color,
    textColor: selectedStore?.primary_color || "#762105",
    backgroundImage: null,
    displayName: user?.displayName || "",
    barcodeType: "qr",
    supportEmail: user?.email || "",
    initialPoints: 2,
    stampCount: 8,
    stampRewardMode: "milestone",
    milestones: [{ position: 8, label: "", _uid: newMilestoneId() }],
    validUntilDateFormat: "en-GB",
    validUntil: new Date(new Date().setMonth(new Date().getMonth() + 12))
      .toISOString()
      .slice(0, 10),
    formattedValidUntil: "",
    enableTier: false,
    tier: {
      label: "",
      value: "Bronze",
    },
    campaign: {
      label: "Reward",
      value: "Free Coffee",
    },
    targetPassId: "none",
    targetAmount: null,
    // Coupon only: grant the target card its configured initial
    // stamps/points when the coupon converts. Off by default.
    applyInitialBonusOnConversion: false,
    stampIcon: null,
    stampIconColor: selectedStore?.background_color,
    useCustomTerms: false,
    termsOfServiceUrl: "",
    enableCustomField: false,
    customField: null,
    selectedCustomFieldId: null,
    selectedCustomField: null,
    passExpirationType: "fixed",
    passExpirationDynamic: 1,
    passExpirationDynamicUnit: "years",
    // Dynamic by default. A fixed date goes stale: once it passes, every
    // point earned on the card is expired on creation, and nothing tells the
    // merchant. Most merchants who enable expiry want "N after earned" anyway.
    pointsExpirationType: "dynamic",
    pointsExpirationDynamic: 1,
    pointsExpirationDynamicUnit: "years",
    pointsValidUntil: new Date(new Date().setMonth(new Date().getMonth() + 12))
      .toISOString()
      .slice(0, 10),
    enablePassExpiration: false,
    enablePointsExpiration: false,
    headerField: "none",
    leftField: "member",
    rightField: "rewards",
    discount: 5,
    preview: {
      headerField: {
        label: "",
        value: "",
      },
      leftField: {
        label: "MEMBER",
        value: user?.displayName || "",
      },
      rightField: {
        label: "REWARDS",
        value: 1,
      },
    },
    enableReviewFeedbackLink: false,
    reviewFeedback: {
      label: "Review & Feedback",
      url: "",
    },
    enableReferalProgram: false,
    referralProgram: {
      type: "use",
      referrerPoints: 0,
      refereePoints: 0,
    },
    enableLinks: false,
    links: [
      {
        label: "",
        url: "",
      },
    ],
    enableCustomBackFields: false,
    customBackFields: [
      {
        label: "",
        value: "",
      },
    ],
  });

  // Update preview
  const updatePreview = () => {
    const headerFieldLabels = {
      none: {
        label: "",
        value: "",
      },
      rewards: {
        label: wl.rewards,
        value: rewardFieldPreview(
          formData.initialPoints,
          formData.milestones,
          t,
        ),
      },
      stamps: {
        label: wl.stamps,
        value: formData.initialPoints ?? "",
      },
      rewards_stamps: {
        label: wl.stamps,
        value: formData.initialPoints,
      },
      points: {
        label: wl.points,
        value: formData.initialPoints,
      },
      balance: {
        label: wl.balance,
        value: formData.initialPoints,
      },
      balance_money: {
        label: wl.balance_money,
        value: `${selectedStore?.currency} ${formData.initialPoints}`,
      },
      visits: {
        label: wl.visits,
        value: formData.initialPoints,
      },
      tier: {
        label: formData.tier.label || wl.tier,
        value: formData.tier.value || "",
      },
      valid_until: {
        label: wl.valid_until,
        value: formData.formattedValidUntil || "",
      },
      discount: {
        label: wl.discount,
        value: `${formData.discount}%` || "",
      },
    };

    // `?? ""` on the initialPoints fields below, never `|| ""`: a prepaid card
    // starts on 0 credit by default, and 0 is falsy — so `||` swapped a real
    // balance for an empty string and the preview rendered the label with no
    // value under it. The nullish check still guards an unset field.
    const secondaryFieldLabels = {
      none: {
        label: "",
        value: "",
      },
      member: {
        label: wl.member,
        value: user?.displayName || "",
      },
      birthday: {
        label: wl.birthday,
        value: "01/01/1990",
      },
      rewards: {
        label: wl.rewards,
        value: rewardFieldPreview(
          formData.initialPoints,
          formData.milestones,
          t,
        ),
      },
      stamps: {
        label: wl.stamps,
        value: formData.initialPoints ?? "",
      },
      valid_until: {
        label: wl.valid_until,
        value: formData.formattedValidUntil || "",
      },
      tier: {
        label: formData.tier.label || wl.tier,
        value: formData.tier.value || "",
      },
      balance: {
        label: wl.balance,
        value: formData.initialPoints ?? "",
      },
      balance_money: {
        label: wl.balance_money,
        value: `${selectedStore?.currency} ${formData.initialPoints}`,
      },
      visits: {
        label: wl.visits,
        value: formData.initialPoints || "0",
      },
      points: {
        label: wl.points,
        value: formData.initialPoints ?? "",
      },
      discount: {
        label: wl.discount,
        value: `${formData.discount}%` || "",
      },
    };

    if (headerFieldLabels[formData.headerField]) {
      setFormData((prev) => ({
        ...prev,
        preview: {
          ...prev.preview,
          headerField: {
            label: headerFieldLabels[prev.headerField].label,
            value: headerFieldLabels[prev.headerField].value.toString(),
          },
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        preview: {
          ...prev.preview,
          headerField: {
            label: formData.headerField,
            value: "{Response}",
          },
        },
      }));
    }

    if (secondaryFieldLabels[formData.leftField]) {
      setFormData((prev) => ({
        ...prev,
        preview: {
          ...prev.preview,
          leftField: {
            label: secondaryFieldLabels[prev.leftField].label,
            value: secondaryFieldLabels[prev.leftField].value.toString(),
          },
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        preview: {
          ...prev.preview,
          leftField: {
            label: formData.leftField,
            value: "{Response}",
          },
        },
      }));
    }

    if (secondaryFieldLabels[formData.rightField]) {
      setFormData((prev) => ({
        ...prev,
        preview: {
          ...prev.preview,
          rightField: {
            label: secondaryFieldLabels[prev.rightField].label,
            value: secondaryFieldLabels[prev.rightField].value.toString(),
          },
        },
      }));
    } else {
      if (formData.type != "coupon") {
        setFormData((prev) => ({
          ...prev,
          preview: {
            ...prev.preview,
            rightField: {
              label: formData.rightField,
              value: "{Response}",
            },
          },
        }));
      }
    }
  };

  useEffect(() => {
    updatePreview();
  }, [
    formData.initialPoints,
    formData.discount,
    formData.validUntil,
    formData.validUntilDateFormat,
    formData.headerField,
    formData.leftField,
    formData.rightField,
    formData.formattedValidUntil,
    formData.tier,
    formData.milestones,
    wlSignature,
  ]);

  const queryClient = useQueryClient();

  const createPassLayoutMutation = useMutation({
    mutationFn: async ({
      storeId,
      layoutData,
      companyLogo,
      stampIcon,
      backgroundImagesFormData,
      milestoneIconsFormData,
      previewImage,
    }) => {
      return createPassLayout({
        storeId,
        layoutData,
        stampIcon,
        companyLogo,
        backgroundImagesFormData,
        milestoneIconsFormData,
        previewImage,
      });
    },
    onSuccess: () => {
      // The new card belongs in the passes list every other screen reads.
      // EditPass has always done this; create only ever named a setup key.
      queryClient.invalidateQueries({
        queryKey: ["passes", selectedStore?.id],
      });
      // "setup-status" is the key Setup.jsx and useSetupStatus read, so this is
      // what has to be invalidated for the wizard to see has_pass flip. It used
      // to name a second key that nothing reads.
      queryClient.invalidateQueries({
        queryKey: ["setup-status", selectedStore?.id],
      });
      if (window.location.pathname.includes("/setup")) {
        navigate("/setup");
      } else {
        navigate("/passes");
      }
    },
  });

  const [cropModalState, setCropModalState] = useState({
    isOpen: false,
    type: CROP_TYPES.BACKGROUND,
    imageUrl: null,
    milestoneId: null,
  });

  const handleImageSelect = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCropModalState({
          isOpen: true,
          type,
          imageUrl: reader.result,
        });
      };
      reader.readAsDataURL(file);
      e.target.value = "";
    }
  };

  const handleCropComplete = (croppedUrl) => {
    switch (cropModalState.type) {
      case CROP_TYPES.BACKGROUND:
        setFormData((prev) => ({ ...prev, backgroundImage: croppedUrl }));
        break;
      case CROP_TYPES.LOGO:
        setFormData((prev) => ({ ...prev, companyLogo: croppedUrl }));
        break;
      case CROP_TYPES.STAMP:
        setFormData((prev) => ({ ...prev, stampIcon: croppedUrl }));
        break;
      case CROP_TYPES.MILESTONE:
        setFormData((prev) => ({
          ...prev,
          milestones: (prev.milestones || []).map((m) =>
            m._uid === cropModalState.milestoneId
              ? { ...m, icon: croppedUrl }
              : m,
          ),
        }));
        break;
    }
  };

  const handleMilestoneImageSelect = (file, milestoneId) =>
    openMilestoneIconCrop(file, milestoneId, setCropModalState);

  const handleBackgroundChange = (e) =>
    handleImageSelect(e, CROP_TYPES.BACKGROUND);
  const handleStampIconChange = (e) => handleImageSelect(e, CROP_TYPES.STAMP);

  const { data: passes } = useQuery({
    queryKey: ["passes", selectedStore?.id],
    queryFn: () => fetchPasses(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const [stampedPreviews, setStampedPreviews] = useState([]);

  const [isSaving, setIsSaving] = useState(false);

  const handleSaveLayout = async (e) => {
    e.preventDefault();

    // Set here rather than on a button: both this page's header button and the
    // one inside PassForm route through here, so every save path reveals the
    // inline milestone errors, including any button added later.
    setSaveAttempted(true);

    if (!validateMilestonesForSave(formData, t, showNotification)) return;
    if (!validateTierForSave(formData, t, showNotification)) return;
    if (!validateCouponForSave(formData, t, showNotification)) return;

    setIsSaving(true);

    try {
      // Check if store is in trial period (15 days from creation)
      const isInTrialPeriod = () => {
        if (!selectedStore?.trial_expires_at) return false;
        const trialEndDate = new Date(selectedStore.trial_expires_at);
        trialEndDate.setDate(trialEndDate.getDate());
        return new Date() < trialEndDate;
      };

      // Get current pass layouts count
      const { data: passes = [] } = await queryClient.fetchQuery({
        queryKey: ["passes", selectedStore?.id],
        queryFn: () => fetchPasses(selectedStore?.id),
      });

      // Check subscription limits
      if (
        selectedStore?.subscription_status !== "active" &&
        selectedStore?.subscription_status !== "past_due" &&
        selectedStore?.subscription_status !== "trialing" &&
        !isInTrialPeriod() &&
        selectedStore?.setup_completed
      ) {
        setIsTrialEndedAlertOpen(true);
        return;
      }

      if (
        selectedStore?.subscription_status === "active" ||
        selectedStore?.subscription_status === "past_due" ||
        selectedStore?.subscription_status === "trialing"
      ) {
        if (selectedStore.current_plan === "starter" && passes.length >= 1) {
          alert(t("passes.subscription.starterCardLimit"));
          navigate("/passes");
          return;
        }
        if (selectedStore.current_plan === "growth" && passes.length >= 4) {
          alert(t("passes.subscription.growthCardLimit"));
          navigate("/passes");
          return;
        }
      }

      // Generate default background image if not provided
      const createDefaultBackgroundImage = (color) => {
        return new Promise((resolve) => {
          const canvas = document.createElement("canvas");
          canvas.width = 1140;
          canvas.height = 435;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = color || formData.textColor || "#762105";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => resolve(blob), "image/png");
        });
      };

      let companyLogo = null;
      const storeLogoUrl =
        selectedStore?.logo_url || "https://placehold.co/64x64";
      if (formData.companyLogo !== storeLogoUrl) {
        const response = await fetch(formData.companyLogo);
        companyLogo = await response.blob();
      }

      let stampIcon = null;
      if (getStampIconSource()) {
        const response = await fetch(getStampIconSource());
        stampIcon = await response.blob();
      }

      const backgroundImagesFormData = new FormData();

      let index = 0;

      // Wait for preview element to be available in DOM
      const waitForElement = (getter, maxAttempts = 20) =>
        new Promise((resolve) => {
          let attempts = 0;
          const check = () => {
            const el = getter();
            if (el) return resolve(el);
            if (++attempts >= maxAttempts) return resolve(null);
            requestAnimationFrame(check);
          };
          check();
        });

      if (formData.type === "stamp") {
        const stampPreview =
          previewRef.current ||
          (await waitForElement(() => previewRef.current));

        if (!stampPreview) {
          window.Intercom?.(
            "showNewMessage",
            "I'm having trouble saving my card design. Can you help?",
          );
          setIsSaving(false);
          return;
        }

        // Generate stamped backgrounds
        const stampedBlobs = await generateStampedImages(
          stampPreview,
          parseInt(formData.stampCount),
          {
            stampIconColor: formData.stampIconColor,
            backgroundColor: formData.backgroundColor,
          },
        );

        // Create preview URLs
        const previewUrls = createPreviewUrls(stampedBlobs);
        setStampedPreviews(previewUrls);

        // Append stamped images to form data
        appendStampedImages(backgroundImagesFormData, stampedBlobs, index);
        index += stampedBlobs.length;
      }

      let backgroundImage;

      if (formData.backgroundImage) {
        const response = await fetch(formData.backgroundImage);
        backgroundImage = await response.blob();
      } else {
        // Create default background image with text color
        backgroundImage = await createDefaultBackgroundImage(
          formData.textColor,
        );
      }

      backgroundImagesFormData.append(
        `backgroundImage_${index}`,
        backgroundImage,
        `background-${index}.png`,
      );

      const previewElement = await waitForElement(
        () =>
          previewRef.current?.closest(".pass-preview-container") ||
          document.querySelector(".pass-preview-container"),
      );

      if (!previewElement) {
        window.Intercom?.(
          "showNewMessage",
          "I'm having trouble saving my card design. Can you help?",
        );
        setIsSaving(false);
        return;
      }

      const previewImageData = await generatePreviewImage(previewElement);
      const previewResponse = await fetch(previewImageData);
      const previewImage = await previewResponse.blob();

      const { layoutData, milestoneIconsFormData } =
        await extractMilestoneIconUploads(formData);

      await createPassLayoutMutation.mutateAsync({
        storeId: selectedStore.id,
        layoutData,
        stampIcon,
        companyLogo,
        backgroundImagesFormData,
        milestoneIconsFormData,
        previewImage,
      });
    } catch (error) {
      if (error.response?.data?.errors) {
        // Join multiple errors into a single string
        const errorMessage = error.response.data.errors.join("\n");
        alert(errorMessage);
      } else {
        // Unexpected client-side failure (e.g. a tainted-canvas SecurityError
        // when the logo/background can't be exported). These are caught here,
        // so the global handler never sees them — report explicitly.
        console.log(error);
        notifyError(error, (event) => {
          event.context = "NewPass.handleSaveLayout";
        });
        alert(error.message);
        // alert(t('passes.form.error.generic'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      stampedPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [stampedPreviews]);

  const previewRef = useRef(null);

  const getStampIconSource = () => stampIconSource(formData);

  const getHeaderFieldLabel = (type) => {
    if (type === "expiration") {
      return "VALID UNTIL";
    }

    if (type === "coupon") {
      return "VALID UNTIL";
    }
    if (type === "membership") {
      return "POINTS";
    }
    if (type === "stamp") {
      return "STAMPS";
    }

    return "STAMPS";
  };

  const getHeaderFieldValue = (type) => {
    if (type === "expiration") {
      return formData.validUntil;
    }

    if (type === "coupon") {
      return formData.validUntil;
    }

    if (type === "membership") {
      return 200;
    }

    if (type === "stamp") {
      return 2;
    }
    return 2;
  };

  const getInitialPoints = (type) => {
    if (type === "POINTS") {
      return 200;
    }
    if (type === "BALANCE") {
      return 0;
    }
    return 2;
  };

  // Function to get the default terms of service text with variables filled in
  const getDefaultTermsOfService = () => {
    return t("passes.form.termsOfService.defaultTerms", {
      storeName: selectedStore?.name,
      email: formData.supportEmail,
    });
  };

  // Add state for collapse
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);
  const [isLayoutOrderOpen, setIsLayoutOrderOpen] = useState(false);

  useEffect(() => {
    if (passes?.length > 0) {
      const names = passes.map((pass) => pass.name);
      const numbers = names
        .filter((name) => name.startsWith("Card #"))
        .map((name) => parseInt(name.replace("Card #", "")))
        .filter((num) => !isNaN(num));
      const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
      const nextName = `Card #${nextNumber}`;

      setFormData((prev) => ({ ...prev, name: nextName }));
    }
  }, [passes]);

  return (
    <div className="max-w-[1800px] mx-auto space-y-6 lg:space-y-8">
      <Header
        title={t("passes.new.title")}
        description={t("passes.new.description")}
        rightSection={
          <Button onClick={handleSaveLayout} isSaving={isSaving}>
            {t("passes.new.button")}
          </Button>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_440px] 2xl:grid-cols-2 gap-6 lg:gap-8">
        <RecoverableBoundary>
          <PassForm
            formData={formData}
            setFormData={setFormData}
            isSaving={isSaving}
            handleSaveLayout={handleSaveLayout}
            saveAttempted={saveAttempted}
            handleBackgroundChange={handleBackgroundChange}
            handleStampIconChange={handleStampIconChange}
            isAdvancedSettingsOpen={isAdvancedSettingsOpen}
            setIsAdvancedSettingsOpen={setIsAdvancedSettingsOpen}
            isLayoutOrderOpen={isLayoutOrderOpen}
            setIsLayoutOrderOpen={setIsLayoutOrderOpen}
            passes={passes}
            useCustomTerms={useCustomTerms}
            setUseCustomTerms={setUseCustomTerms}
            termsUrl={termsUrl}
            setTermsUrl={setTermsUrl}
            setIsTermsModalOpen={setIsTermsModalOpen}
            defaultBackground={defaultBackground}
            getHeaderFieldLabel={getHeaderFieldLabel}
            getHeaderFieldValue={getHeaderFieldValue}
            getInitialPoints={getInitialPoints}
            selectedStore={selectedStore}
            onMilestoneImageSelect={handleMilestoneImageSelect}
          />
        </RecoverableBoundary>

        <RecoverableBoundary>
          <PassPreview
            formData={formData}
            previewRef={previewRef}
            getStampIconSource={getStampIconSource}
          />
        </RecoverableBoundary>

        <ImageCropModal
          isOpen={cropModalState.isOpen}
          onClose={() =>
            setCropModalState((prev) => ({ ...prev, isOpen: false }))
          }
          imageUrl={cropModalState.imageUrl}
          type={cropModalState.type}
          title={getCropModalTitle(cropModalState.type, t)}
          saveLabel={t("passes.form.background.crop.save")}
          cancelLabel={t("passes.form.background.crop.cancel")}
          onCropComplete={handleCropComplete}
        />

        <TrialEndedAlert
          isOpen={isTrialEndedAlertOpen}
          onClose={() => setIsTrialEndedAlertOpen(false)}
          subscriptionStatus={selectedStore?.subscription_status}
        />

        {/* Terms of Service Modal */}
        <Transition appear show={isTermsModalOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-60"
            onClose={() => setIsTermsModalOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/40" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 mb-4"
                    >
                      {t("passes.form.termsOfService.modalTitle")}
                    </Dialog.Title>
                    <div className="mt-2">
                      <div className="prose prose-sm prose-pre:bg-gray-100 prose-pre:text-gray-900 max-h-[60vh] overflow-y-auto">
                        <div
                          className="text-sm"
                          dangerouslySetInnerHTML={{
                            __html: getDefaultTermsOfService(),
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-900 hover:bg-indigo-200"
                        onClick={() => setIsTermsModalOpen(false)}
                      >
                        {t("common.close")}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
}
